import { createContext } from "react";
import { AddTaskRelationData } from "../../../taskManagement/createTaskPane/CreateTaskPane.types.ts";
import { DotLegalOverflowMenuItem } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { usePanes } from "../../../main/panes/usePanes";
import CreateTaskPane from "../../../taskManagement/createTaskPane/CreateTaskPane";
import { Box } from "@mui/material";
import PurpleLockSvg from "../../svgs/purpleLock.svg?react";

export interface ExtendedDotLegalOverflowMenuItem extends DotLegalOverflowMenuItem {
    planMenuItemInfo?: {
        tooltip: string;
        menuName: string;
        hasAccess: boolean;
    };
}

export interface AddTaskMenItemModel {
    addCreateTaskMenuItem: (
        menuItems: Array<ExtendedDotLegalOverflowMenuItem>,
        relationInformation: AddTaskRelationData,
        namePrefix?: string,
        isTableRow?: boolean
    ) => void;
}

export const AddTaskMenuItemContext = createContext<AddTaskMenItemModel>({
    addCreateTaskMenuItem: () => {},
});

export interface IAddTaskMenItemProviderProps {
    children: any;
}

/**
 * Adds "Create task" to an entity's overflow menu. It used to own the create dialog and the dialog that
 * followed it, along with a refetch slot for the page underneath; the card takes all of that, so what is
 * left is the menu item itself. The page behind refreshes through the panel's own registry instead.
 */
export function AddTaskMenuItemProvider(props: IAddTaskMenItemProviderProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures, addonsFeatures } = usePlanContext();
    const { open } = usePanes();

    const data: AddTaskMenItemModel = {
        addCreateTaskMenuItem: (menuItems, relationInformation, namePrefix, isTableRow) => {
            if (permissions.taskPermissions.create && !addonsFeatures.research) {
                menuItems.push({
                    menuItem:
                        isTableRow && !platformFeatures.canAccessRelationsBetweenEntities ? (
                            <Box sx={{ display: "flex" }}>
                                <span>{translateString("createTask")}</span>
                                <Box sx={{ marginLeft: 0.5 }}>
                                    <PurpleLockSvg />
                                </Box>
                            </Box>
                        ) : (
                            translateString("createTask")
                        ),
                    onClick: () => open(CreateTaskPane, { relationInformation, prefixName: namePrefix }),
                    planMenuItemInfo: {
                        hasAccess: platformFeatures.canAccessRelationsBetweenEntities,
                        menuName: translateString("createTask"),
                        tooltip: translateString("upgradePrivacyPlanToday"),
                    },
                    disabled: !platformFeatures.canAccessRelationsBetweenEntities,
                    tooltip: !platformFeatures.canAccessRelationsBetweenEntities ? translateString("upgradePrivacyPlanToday") : undefined,
                    tooltipPosition: "left",
                });
            }
        },
    };

    return <AddTaskMenuItemContext.Provider value={data}>{props.children}</AddTaskMenuItemContext.Provider>;
}

export default AddTaskMenuItemProvider;
